// Pure helpers for the campaign form: the blank form, value formatters, and the
// mapping from a saved campaign record into form state.

import { REACT_APP_API_BASE_URL } from '../../apiurl';

// Turns a stored image path ("/uploads/campaigns/x.png") into a loadable URL by
// prefixing the API origin, so thumbnails render both locally and on the server.
export const assetUrl = (fileUrl) => {
    const v = String(fileUrl || '').trim();
    if (/^https?:\/\//i.test(v)) return v;
    return `${REACT_APP_API_BASE_URL}${v.replace(/^\//, '')}`;
};

export const initialFormData = {
    id: '',
    placement_name: '',
    creative_type: 'banner',
    creative_html: '',
    // No redirect by default — a tap does nothing until an action/URL is chosen.
    target_action: 'none',
    target_user_group: 'all',
    priority: 1,
    sponsor_name: '',
    notes: '',
    activation_timestamp: '',
    deactivation_timestamp: '',
    status: 'inactive',
    // Auto-hide duration entered as a value + unit; converted to ms on submit.
    duration_value: '',
    duration_unit: 'seconds',
    // In-feed controls (only meaningful for *_feed placements).
    feed_position: '',
    feed_repeat: '',
    // Close (X) button visibility and delay; the delay converts to ms on submit.
    show_close_button: true,
    close_delay_value: '',
    close_delay_unit: 'seconds',
};

// Converts a backend ISO/date string into the value a datetime-local input needs.
export const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// True when a value looks like a link (used to pick the Target Action mode).
export const looksLikeUrl = (v) => /^(https?:\/\/|\/uploads\/)/i.test(String(v || '').trim());

// True when a stored asset URL points to a direct video file, so the row preview
// uses a <video> element instead of <img> (which would show a broken thumbnail).
export const isVideoUrl = (v) => /\.(mp4|webm|mov)(\?|$)/i.test(String(v || '').trim());

// True when the URL is a YouTube/Vimeo embed (backend-normalized form).
export const isEmbedUrl = (v) => /(?:youtube\.com\/embed\/|player\.vimeo\.com\/video\/)/i.test(String(v || '').trim());

// A poster image for an embed preview: YouTube exposes a thumbnail by id; Vimeo
// does not without an API call, so it falls back to no poster (an icon tile).
export const embedPoster = (v) => {
    const m = String(v || '').match(/youtube\.com\/embed\/([A-Za-z0-9_-]{11})/i);
    return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
};

// True when the creative content is raw HTML rather than image URLs.
export const looksLikeHtml = (v) => /<[a-z!/][\s\S]*>/i.test(String(v || ''));

// Pretty label for a stored slug — "open_contact_form" → "Open Contact Form".
// Display only; the underlying value the backend stores is never changed.
export const titleCase = (v) => String(v || '')
    .split(/[_\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');

// Splits a stored millisecond value back into the number + unit the form shows.
// Whole minutes display as minutes; anything else as seconds. Zero/absent leaves
// the field blank.
const msToValueUnit = (raw) => {
    const ms = Number(raw);
    if (!Number.isFinite(ms) || ms <= 0) return { value: '', unit: 'seconds' };
    if (ms % 60000 === 0) return { value: String(ms / 60000), unit: 'minutes' };
    return { value: String(ms / 1000), unit: 'seconds' };
};

// Builds the full form state from a campaign record — used both when seeding
// from the already-loaded list row (instant, no fetch) and from a fallback fetch.
export const buildFormState = (data) => {
    const items = Array.isArray(data.creative_items) ? data.creative_items : [];
    const isHtml = items.length === 0 && looksLikeHtml(data.creative_html);
    const duration = msToValueUnit(data.display_duration_ms);
    const closeDelay = msToValueUnit(data.close_button_delay_ms);
    const creativeItems = items.map((it) => ({
        asset_id: it.asset_id,
        file_url: it.file_url,
        alt_text: it.alt_text || '',
        redirect_url: it.redirect_url || '',
    }));
    const formData = {
        id: data.id ?? '',
        placement_name: data.placement_name || '',
        creative_type: data.creative_type || 'banner',
        creative_html: isHtml ? (data.creative_html || '') : '',
        target_action: data.target_action || 'none',
        target_user_group: data.target_user_group || 'all',
        priority: data.priority ?? 1,
        sponsor_name: data.sponsor_name || '',
        notes: data.notes || '',
        activation_timestamp: formatDateTime(data.activation_timestamp),
        deactivation_timestamp: formatDateTime(data.deactivation_timestamp),
        status: data.status || 'inactive',
        duration_value: duration.value,
        duration_unit: duration.unit,
        feed_position: data.feed_position != null ? String(data.feed_position) : '',
        feed_repeat: data.feed_repeat != null ? String(data.feed_repeat) : '',
        show_close_button: data.show_close_button !== false,
        close_delay_value: closeDelay.value,
        close_delay_unit: closeDelay.unit,
    };
    return {
        formData,
        creativeItems,
        creativeMode: isHtml ? 'html' : 'image',
        actionMode: looksLikeUrl(data.target_action) ? 'url' : 'action',
        baseline: JSON.stringify({ fd: formData, items: creativeItems }),
    };
};
